import React, { useCallback, useEffect, useMemo } from "react";
import { Button, Checkbox, Form, FormField, FormGroup, Icon } from "semantic-ui-react";

// Utils
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import {
  appointmentStatusType,
  BTN_ACTS,
  PickedState,
  serviceStatusType,
  SetProp,
  State,
} from "react-lib/apps/MEH/sequence/AppointmentListMEH";
import { DateTextBox } from "react-lib/apps/common";
import TimeTextBox from "react-lib/apps/common/TimeTextBox";
import TimeFreeTextBox24 from "react-lib/apps/common/TimeFreeTextBox24";
import { statusStyles } from "./CardAppointmentListMEH";
import SubPackageOrderDetail from "./SubPackageOrderDetail";
import { formatADtoBEString } from "react-lib/utils/dateUtils";

type CardAppointmentDetailMEHProps = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  setProp: SetProp;
  runSequence: any;

  // sequence
  AppointmentListMEHSequence?: State["AppointmentListMEHSequence"];
  MakeAppointmentMeHeySequence: any;
  // CommonInterface
  availableSlots?: Record<string, any>[];
  onOpenModDetail?: (type: boolean) => void;
  onUpdateAppointment?: () => void;
  onOpenPackageDetail?: (packageId: number) => void;
  languageUX?: string;
};

const CardAppointmentDetailMEH = (props: CardAppointmentDetailMEHProps) => {
  useEffect(() => {
    props.runSequence({ sequence: "MakeAppointmentMeHey", restart: true });
    return () => {
      props.runSequence({ sequence: "MakeAppointmentMeHey", clear: true });
    };
  }, []);

  const timeToDecimal = useCallback((timeStr?: string) => {
    if (!timeStr) {
      return 0;
    }

    const [hours, minutes] = timeStr.split(":");

    return Number.parseInt(hours) + Number.parseInt(minutes) / 60;
  }, []);

  const dsbData = useMemo(() => {
    const dsb = props.MakeAppointmentMeHeySequence?.dsb;

    if (!Array.isArray(dsb) || dsb.length === 0) return [];

    return [
      ...new Set(
        dsb
          .filter(
            (item) =>
              item?.providers?.[0]?.dsb?.doctor_provider_name ===
              props.AppointmentListMEHSequence?.appointmentDetail?.doctor_full_name,
          )
          .map((item) => (item?.date ? formatADtoBEString(item.date) : undefined))
          .filter((date): date is string => !!date),
      ),
    ];
  }, [
    props.MakeAppointmentMeHeySequence?.dsb,
    props.AppointmentListMEHSequence?.appointmentDetail?.doctor_full_name,
  ]);

  return (
    <div>
      <Form>
        {[
          {
            label: "BookingID",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.id,
            style: {},
          },
          {
            label: "ชื่อ - นามสกุลผู้รับบริการ",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.patient,
            style: {},
          },
          {
            label: "เบอร์โทร",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.tel_text,
            style: {},
          },
          {
            label: "วันที่นัดหมาย",
            value: "",
            style: {},
          },
          {
            label: "ผู้ให้บริการ",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.doctor_full_name,
            style: {},
          },
          {
            label: "สถานะนัดหมาย",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.appointment_status_text,
            style: {
              borderRadius: "40px",
              padding: "3px 12px",
              textAlign: "center",
              fontSize: "14px",
              width: "max-content",
              ...statusStyles[
                props.AppointmentListMEHSequence?.appointmentDetail
                  ?.appointment_status_text as appointmentStatusType
              ],
            },
          },
          {
            label: "อาการเบื้องต้น",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.order_note,
            style: {},
          },
          {
            label: "สถานะรับบริการ",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.service_status_text,
            style: {
              borderRadius: "40px",
              padding: "3px 12px",
              textAlign: "center",
              fontSize: "14px",
              width: "max-content",
              ...statusStyles[
                props.AppointmentListMEHSequence?.appointmentDetail
                  ?.service_status_text as serviceStatusType
              ],
            },
          },
          {
            label: "วันที่รับบริการ",
            value: props.AppointmentListMEHSequence?.appointmentDetail?.service_date_text,
            style: {},
          },
          ...(props.AppointmentListMEHSequence?.appointmentDetail?.appointment_status_text ===
          "ยกเลิกนัดหมาย"
            ? [
                {
                  label: "สาเหตุที่ยกเลิกนัดหมาย",
                  value: props.AppointmentListMEHSequence?.appointmentDetail?.status_note || "",
                  style: {},
                },
                {
                  label: "ผู้ยกเลิกนัดหมาย",
                  value:
                    props.AppointmentListMEHSequence?.appointmentDetail?.edit_user_employee_name,
                  style: {},
                },
              ]
            : []),
          ...(props.AppointmentListMEHSequence?.appointmentDetail?.package_orders
            ? [
                {
                  label: "รายการนัดหมายที่รับบริการ",
                  value: "",
                  style: {},
                },
              ]
            : []),
        ].map((field, idx) => (
          <FormGroup key={idx} style={{ margin: "0px 5px 20px 5px" }}>
            <FormField width={4}>
              <label>{field.label}</label>
            </FormField>
            <FormField width={12} style={{ width: "max-content" }}>
              {field.label === "วันที่นัดหมาย" ? (
                <Form>
                  <FormGroup>
                    <FormField width={4}>
                      <div>
                        <DateTextBox
                          disabled={
                            props.AppointmentListMEHSequence?.appointmentDetail
                              ?.appointment_status_text === "ยกเลิกนัดหมาย" ||
                            props.AppointmentListMEHSequence?.appointmentDetail
                              ?.service_status_text === "รับบริการแล้ว"
                          }
                          onMonthChange={(month: any) => {
                            props.runSequence({
                              sequence: "MakeAppointmentMeHey",
                              action: "SET_CURRENT_DATE",
                              currentDate: month,
                            });
                          }}
                          disabledDays={dsbData}
                          onChange={(date: any) => {
                            props.setProp(
                              `AppointmentListMEHSequence.appointmentDetail.date`,
                              date,
                            );
                          }}
                          value={props.AppointmentListMEHSequence?.appointmentDetail?.date || ""}
                        ></DateTextBox>
                      </div>
                    </FormField>
                    <FormField>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div style={{ fontWeight: "bolder", marginRight: "10px" }}>
                          เวลานัดหมาย :
                        </div>
                        {props.AppointmentListMEHSequence?.appointmentDetail?.manualTime ? (
                          <div>
                            <TimeFreeTextBox24
                              className="time-custom-react-picker"
                              minHour={
                                timeToDecimal(
                                  props.AppointmentListMEHSequence?.appointmentDetail?.minHour,
                                ) || 0
                              }
                              maxHour={
                                timeToDecimal(
                                  props.AppointmentListMEHSequence?.appointmentDetail?.maxHour,
                                ) || 23
                              }
                              onChange={(value: any) => {
                                props.setProp(
                                  `AppointmentListMEHSequence.appointmentDetail.estimatedAt`,
                                  value,
                                );
                              }}
                              disabled={
                                props.AppointmentListMEHSequence?.appointmentDetail
                                  ?.appointment_status_text === "ยกเลิกนัดหมาย" ||
                                props.AppointmentListMEHSequence?.appointmentDetail
                                  ?.service_status_text === "รับบริการแล้ว"
                              }
                              value={
                                props.AppointmentListMEHSequence?.appointmentDetail?.estimatedAt
                              }
                            ></TimeFreeTextBox24>
                          </div>
                        ) : (
                          <div>
                            <TimeTextBox
                              availableOptions={props.availableSlots}
                              interval={
                                props.AppointmentListMEHSequence?.appointmentDetail
                                  ?.intervalTimeSelected || 60
                              }
                              minHour={
                                timeToDecimal(
                                  props.AppointmentListMEHSequence?.appointmentDetail?.minHour,
                                ) || 0
                              }
                              maxHour={
                                timeToDecimal(
                                  props.AppointmentListMEHSequence?.appointmentDetail?.maxHour,
                                ) || 23
                              }
                              onTimeChange={(e: any, value: any) => {
                                props.setProp(
                                  `AppointmentListMEHSequence.appointmentDetail.estimatedAt`,
                                  value,
                                );
                              }}
                              disabled={
                                props.AppointmentListMEHSequence?.appointmentDetail
                                  ?.appointment_status_text === "ยกเลิกนัดหมาย" ||
                                props.AppointmentListMEHSequence?.appointmentDetail
                                  ?.service_status_text === "รับบริการแล้ว"
                              }
                              reservedText={"(มีนัดหมาย)"}
                              useDefaultTime={true}
                              defaultTime={
                                props.AppointmentListMEHSequence?.appointmentDetail?.estimatedAt
                              }
                              value={
                                props.AppointmentListMEHSequence?.appointmentDetail?.estimatedAt
                              }
                            ></TimeTextBox>
                          </div>
                        )}
                      </div>
                    </FormField>
                    <FormField style={{ display: "flex", alignItems: "center" }}>
                      <Checkbox
                        checked={props.AppointmentListMEHSequence?.appointmentDetail?.manualTime}
                        label={"กำหนดเวลาแบบ manual"}
                        disabled={
                          props.AppointmentListMEHSequence?.appointmentDetail
                            ?.appointment_status_text === "ยกเลิกนัดหมาย" ||
                          props.AppointmentListMEHSequence?.appointmentDetail
                            ?.service_status_text === "รับบริการแล้ว"
                        }
                        onChange={(e: any, v: any) =>
                          props.setProp(
                            `AppointmentListMEHSequence.appointmentDetail.manualTime`,
                            v.checked,
                          )
                        }
                      ></Checkbox>
                    </FormField>
                  </FormGroup>
                </Form>
              ) : field.label === "รายการนัดหมายที่รับบริการ" ? (
                <SubPackageOrderDetail
                  setProp={props.setProp}
                  packageOrders={
                    props.AppointmentListMEHSequence?.appointmentDetail?.package_orders
                  }
                  packageDetail={props.AppointmentListMEHSequence?.packageDetail || null}
                  buttonLoadCheck={props.buttonLoadCheck}
                  onOpenPackageDetail={props.onOpenPackageDetail}
                />
              ) : (
                <div style={field.style as React.CSSProperties}>{field.value || "-"}</div>
              )}
            </FormField>
          </FormGroup>
        ))}
      </Form>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div>
          <Button
            onClick={() => {
              props.onOpenModDetail?.(false);
              props.setProp("AppointmentListMEHSequence.appointmentDetail", {});
            }}
          >
            ปิดหน้าจอ
          </Button>
        </div>
        <div
          style={{
            ...((props.AppointmentListMEHSequence?.appointmentDetail?.appointment_status_text ===
              "ยกเลิกนัดหมาย" ||
              props.AppointmentListMEHSequence?.appointmentDetail?.service_status_text ===
                "รับบริการแล้ว") && { display: "none" }),
          }}
        >
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={props.onUpdateAppointment}
            // data
            paramKey={BTN_ACTS.UPDATE_APPOINTMENT}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.UPDATE_APPOINTMENT]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "white" }}
            fluid={true}
            size="small"
            title={"บันทึกข้อมูล"}
          />
        </div>
      </div>
    </div>
  );
};

CardAppointmentDetailMEH.displayName = "CardAppointmentDetailMEH";
export default CardAppointmentDetailMEH;
